import React, { useEffect, useState } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import FilterBar from '../components/FilterBar.js';
import BlockedOverlay from '../components/BlockedOverlay.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { useFilters } from '../context/FilterContext.js';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';

import CompetitivenessIndex from '../components/CompetitivenessIndex.js';

const BENEFIT_LABELS = {
  plano_saude: 'Plano de Saúde',
  plano_odontologico: 'Plano Odontológico',
  vale_alimentacao: 'Vale Alimentação',
  vale_refeicao: 'Vale Refeição',
  vale_transporte: 'Vale Transporte',
  seguro_vida: 'Seguro de Vida',
  auxilio_educacao: 'Auxílio Educação',
  gympass: 'Gympass',
  previdencia_privada: 'Previdência Privada',
  ppr_plr: 'PPR/PLR',
};

const fmtR = (v) => typeof v === 'number' ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}` : '--';

const PosTag = ({ diff }) => {
  if (diff === null || diff === undefined || diff === 0) return null;
  const Icon = diff > 5 ? ArrowUp : diff < -5 ? ArrowDown : Minus;
  const cls = diff > 5 ? 'bg-emerald-100 text-emerald-700' : diff < -5 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{diff > 0 ? '+' : ''}{diff}%
    </span>
  );
};

const BenefitCard = ({ label, stat, myItem }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`benefit-${label}`}>
    <p className="text-xs text-slate-500 mb-1">{stat?.label || label}</p>
    <p className="text-xl font-bold text-slate-900">{fmtR(stat?.media)}</p>
    <div className="flex gap-2 mt-1.5 text-[10px] text-slate-400">
      <span>1ºQ: {fmtR(stat?.p25)}</span>
      <span>3ºQ: {fmtR(stat?.p75)}</span>
    </div>
    {myItem && myItem.valor > 0 && (
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <span className="text-[10px] text-slate-500">Sua Empresa: <strong className="text-slate-800">{fmtR(myItem.valor)}</strong></span>
        <PosTag diff={myItem.diff_pct} />
      </div>
    )}
  </div>
);

const BenefitsDashboard = () => {
  const [data, setData] = useState(null);
  const { fetchWithFilters, filters } = useFilters();
  const userRole = localStorage.getItem('userRole') || 'respondent';

  useEffect(() => {
    const load = async () => {
      try {
        const result = await fetchWithFilters('/benefits/analysis');
        setData(result);
      } catch (e) { console.error(e); }
    };
    load();
  }, [filters, fetchWithFilters]);

  if (!data) return <MainLayout title="Benefícios" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  const blocked = data.blocked;
  const benefits = data.benefits || {};
  const me = data.minha_empresa;
  const meItems = me?.items || {};
  const mePos = me?.posicionamento;

  const chartData = Object.entries(BENEFIT_LABELS).map(([key, label]) => ({
    name: label.length > 14 ? label.slice(0, 14) + '...' : label,
    Mercado: benefits[key]?.media || 0,
    'Sua Empresa': meItems[key]?.valor || 0,
  }));

  const segmentChart = (data.by_segment || []).map(s => ({
    name: s.label.length > 12 ? s.label.slice(0, 12) + '...' : s.label,
    Total: s.total_medio,
  }));

  return (
    <MainLayout title="Benefícios" userRole={userRole}>
      <div className="space-y-6" data-testid="benefits-dashboard">
        <FilterBar n={data.n} blocked={blocked} />

        {blocked ? <BlockedOverlay n={data.n} /> : (
          <>
            <CompetitivenessIndex variant="compact" />

            <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-lg p-6 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm opacity-70">Total Médio de Benefícios (Mercado)</p>
                  <p className="text-4xl font-bold mt-1">{fmtR(benefits.total_beneficios?.media)}</p>
                  <div className="flex gap-6 mt-3 text-sm opacity-60">
                    <span>1º Quartil: {fmtR(benefits.total_beneficios?.p25)}</span>
                    <span>Mediana: {fmtR(benefits.total_beneficios?.p50)}</span>
                    <span>3º Quartil: {fmtR(benefits.total_beneficios?.p75)}</span>
                  </div>
                </div>
                {me && (
                  <div className="bg-white/10 rounded-lg p-4 text-right" data-testid="benefits-my-total">
                    <p className="text-[11px] opacity-60">Sua Empresa</p>
                    <p className="text-2xl font-bold">{fmtR(me.total_beneficios)}</p>
                  </div>
                )}
              </div>
            </div>

            {mePos && mePos.total > 0 && (
              <div className="grid grid-cols-3 gap-3" data-testid="benefits-position-summary">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <ArrowUp className="w-5 h-5 text-emerald-600 mx-auto" />
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{mePos.acima}</p>
                  <p className="text-xs text-emerald-600">acima do mercado ({mePos.pct_acima}%)</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <Minus className="w-5 h-5 text-blue-600 mx-auto" />
                  <p className="text-2xl font-bold text-blue-700 mt-1">{mePos.na_media}</p>
                  <p className="text-xs text-blue-600">na média</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <ArrowDown className="w-5 h-5 text-red-500 mx-auto" />
                  <p className="text-2xl font-bold text-red-600 mt-1">{mePos.abaixo}</p>
                  <p className="text-xs text-red-500">abaixo do mercado ({mePos.pct_abaixo}%)</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(BENEFIT_LABELS).map(([key]) => (
                <BenefitCard key={key} label={key} stat={benefits[key]} myItem={meItems[key]} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Empresa vs Mercado</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `R$${v.toLocaleString('pt-BR')}`} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={95} />
                      <Tooltip formatter={v => fmtR(v)} />
                      <Bar dataKey="Mercado" fill="#2563eb" radius={[0, 2, 2, 0]} />
                      <Bar dataKey="Sua Empresa" fill="#0f172a" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Total por Segmento</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={segmentChart} margin={{ top: 5, right: 20, bottom: 60, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={v => fmtR(v)} />
                      <Bar dataKey="Total" fill="#2563eb" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BenefitsDashboard;
